import { defineStore } from 'pinia';
import http from '../api/httpRequest.js';
import useFriendStore from './friendStore.js';
import { getDB } from '../db/index.js';
import { applyPageState, createPageState, resetPageState } from './listPageState.js';

function sanitizeGroupForPersist(group) {
	if (!group || group.id === undefined || group.id === null) {
		return null;
	}
	const memberCount = Number(group.memberCount);
	return {
		id: group.id,
		name: String(group.name || '').trim(),
		memberCount: Number.isFinite(memberCount) ? memberCount : 0,
		createdTime: group.createdTime || null,
		updatedTime: group.updatedTime || null
	};
}

const FRIEND_GROUP_LIST_FIRST_PAGE_LIMIT = 30;
const FRIEND_GROUP_LIST_PAGE_LIMIT = 30;
const FRIEND_GROUP_LIST_PAGE_LIMIT_MAX = 30;
const FRIEND_GROUP_LIST_BACKGROUND_PAGE_BUDGET = 4;
const FRIEND_GROUP_LIST_FULL_LOAD_PAGE_BUDGET = 500;
const FRIEND_GROUP_MEMBER_PAGE_LIMIT = 30;
const FRIEND_GROUP_MEMBER_BULK_PAGE_LIMIT = 500;
const FRIEND_GROUP_MEMBER_PAGE_LIMIT_MAX = 500;
const FRIEND_GROUP_MEMBER_FULL_LOAD_PAGE_BUDGET = 500;
const FRIEND_GROUP_MEMBER_FULL_LOAD_PAGE_BUDGET_MAX = 5000;

function resolveIdKey(id) {
	if (id === undefined || id === null || id === '') {
		return '';
	}
	return String(id);
}

function normalizeGroup(group) {
	if (!group || typeof group !== 'object') {
		return null;
	}
	const groupKey = resolveIdKey(group.id);
	if (!groupKey) {
		return null;
	}
	const memberCount = Number(group.memberCount);
	return {
		...group,
		id: group.id,
		name: String(group.name || '').trim(),
		memberCount: Number.isFinite(memberCount) ? memberCount : 0
	};
}

function normalizeGroups(groups) {
	return (Array.isArray(groups) ? groups : []).map((group) => {
		const normalizedGroup = normalizeGroup(group);
		if (!normalizedGroup) {
			return null;
		}
		return normalizedGroup;
	}).filter(Boolean);
}

function normalizePageLimit(limit, fallback = 30, max = 30) {
	const value = Number(limit);
	if (!Number.isFinite(value)) {
		return fallback;
	}
	const normalized = Math.floor(value);
	return Math.max(1, Math.min(max, normalized));
}

function normalizePageBudget(value, fallback = 1, max = 100) {
	const num = Number(value);
	if (!Number.isFinite(num)) {
		return fallback;
	}
	const normalized = Math.floor(num);
	return Math.max(1, Math.min(max, normalized));
}

function normalizeFriendPagePayload(payload) {
	const normalized = payload && typeof payload === 'object' ? payload : {};
	const items = Array.isArray(normalized.items) ? normalized.items : [];
	const total = Math.floor(Number(normalized.total));
	return {
		items,
		more: Boolean(normalized.more),
		total: Number.isFinite(total) && total > 0 ? total : 0,
		cursor: normalized.cursor ? String(normalized.cursor) : ''
	};
}

function normalizeGroupPagePayload(payload) {
	const normalized = payload && typeof payload === 'object' ? payload : {};
	const items = Array.isArray(normalized.items) ? normalized.items : [];
	const total = Math.floor(Number(normalized.total));
	return {
		items,
		more: Boolean(normalized.more),
		total: Number.isFinite(total) && total > 0 ? total : 0,
		cursor: normalized.cursor ? String(normalized.cursor) : ''
	};
}

function normalizeFriendVO(friend) {
	if (!friend || typeof friend !== 'object') {
		return null;
	}
	const idKey = resolveIdKey(friend.id);
	if (!idKey) {
		return null;
	}
	return {
		...friend,
		id: friend.id
	};
}

export default defineStore('friendGroupStore', {
	state: () => {
		return {
			groups: [],
			groupMap: new Map(),
			page: createPageState(),
			tasks: {
				load: null,
				bg: null,
				full: null,
				localReady: null
			},
			friendToGroupMap: new Map(),
			friendPageByGroup: new Map(),
			friendCacheByGroup: new Map(),
			friendLoadTaskByGroup: new Map()
		};
	},
	actions: {
		resetGroupListState(options = {}) {
			const clearTasks = options.clearTasks !== false;
			resetPageState(this.page);
			if (clearTasks) {
				this.tasks.load = null;
				this.tasks.bg = null;
				this.tasks.full = null;
			}
		},
		resolveGroupFriendTask(groupId) {
			const groupKey = resolveIdKey(groupId);
			if (!groupKey) {
				return null;
			}
			return this.friendLoadTaskByGroup.get(groupKey) || null;
		},
		setGroupFriendTask(groupId, task) {
			const groupKey = resolveIdKey(groupId);
			if (!groupKey) {
				return null;
			}
			if (!task) {
				this.friendLoadTaskByGroup.delete(groupKey);
				this.friendLoadTaskByGroup = new Map(this.friendLoadTaskByGroup);
				return null;
			}
			this.friendLoadTaskByGroup.set(groupKey, task);
			this.friendLoadTaskByGroup = new Map(this.friendLoadTaskByGroup);
			return task;
		},
		setGroupListPageState(page) {
			applyPageState(this.page, page);
		},
		rebuildMaps() {
			const nextGroupMap = new Map();
			const nextFriendToGroupMap = new Map();
			this.groups.forEach((group) => {
				const groupKey = resolveIdKey(group && group.id);
				if (!groupKey) {
					return;
				}
				nextGroupMap.set(groupKey, group);
				const cachedFriends = Array.isArray(this.friendCacheByGroup.get(groupKey))
					? this.friendCacheByGroup.get(groupKey)
					: [];
				cachedFriends.forEach((friend) => {
					const friendKey = resolveIdKey(friend && friend.id);
					if (!friendKey) {
						return;
					}
					nextFriendToGroupMap.set(friendKey, groupKey);
				});
			});
			const friendStore = useFriendStore();
			(Array.isArray(friendStore.friends) ? friendStore.friends : []).forEach((friend) => {
				const friendKey = resolveIdKey(friend && friend.id);
				const groupKey = resolveIdKey(friend && friend.groupId);
				if (!friendKey || !groupKey || nextFriendToGroupMap.has(friendKey) || !nextGroupMap.has(groupKey)) {
					return;
				}
				nextFriendToGroupMap.set(friendKey, groupKey);
			});
			this.groupMap = nextGroupMap;
			this.friendToGroupMap = nextFriendToGroupMap;
			this.groups.forEach((group) => {
				const groupKey = resolveIdKey(group && group.id);
				if (!groupKey) {
					return;
				}
				const groupMemberCount = Math.max(0, Math.floor(Number(group && group.memberCount) || 0));
				if (!this.friendPageByGroup.has(groupKey)) {
					this.friendPageByGroup.set(groupKey, {
						loading: false,
						more: true,
						total: groupMemberCount,
						cursor: '',
						loaded: false
					});
				} else {
					const pageState = this.friendPageByGroup.get(groupKey) || {};
					this.friendPageByGroup.set(groupKey, {
						...pageState,
						total: groupMemberCount
					});
				}
				if (!this.friendCacheByGroup.has(groupKey)) {
					this.friendCacheByGroup.set(groupKey, []);
				}
			});
			const validGroupKeys = new Set(nextGroupMap.keys());
			Array.from(this.friendPageByGroup.keys()).forEach((groupKey) => {
				if (!validGroupKeys.has(groupKey)) {
					this.friendPageByGroup.delete(groupKey);
				}
			});
			Array.from(this.friendCacheByGroup.keys()).forEach((groupKey) => {
				if (!validGroupKeys.has(groupKey)) {
					this.friendCacheByGroup.delete(groupKey);
				}
			});
			Array.from(this.friendLoadTaskByGroup.keys()).forEach((groupKey) => {
				if (!validGroupKeys.has(groupKey)) {
					this.friendLoadTaskByGroup.delete(groupKey);
				}
			});
			this.friendPageByGroup = new Map(this.friendPageByGroup);
			this.friendCacheByGroup = new Map(this.friendCacheByGroup);
			this.friendLoadTaskByGroup = new Map(this.friendLoadTaskByGroup);
		},
		setGroups(groups) {
			const normalizedGroups = normalizeGroups(groups);
			this.groups = normalizedGroups;
			this.rebuildMaps();
		},
		appendGroups(groups) {
			const normalizedGroups = normalizeGroups(groups);
			if (normalizedGroups.length === 0) {
				return;
			}
			const existingMap = new Map(
				(Array.isArray(this.groups) ? this.groups : []).map((group) => [resolveIdKey(group && group.id), group])
			);
			normalizedGroups.forEach((group) => {
				const groupKey = resolveIdKey(group && group.id);
				if (!groupKey) {
					return;
				}
				const current = existingMap.get(groupKey);
				if (current) {
					Object.assign(current, group);
					return;
				}
				this.groups.push(group);
			});
			this.rebuildMaps();
		},
		upsertGroup(group) {
			const normalizedGroup = normalizeGroup(group);
			if (!normalizedGroup) {
				return;
			}
			const normalizedGroupIdKey = resolveIdKey(normalizedGroup.id);
			const current = this.groupMap.get(normalizedGroupIdKey);
			if (current) {
				Object.assign(current, normalizedGroup);
			} else {
				this.groups.unshift(normalizedGroup);
				this.page.total = Math.max(this.page.total + 1, this.groups.length);
			}
			this.rebuildMaps();
			const persistRecord = sanitizeGroupForPersist(normalizedGroup);
			if (persistRecord) {
				getDB().saveFriendGroup(persistRecord).catch((error) => {
					console.warn('落盘好友分组单条快照失败', error);
				});
			}
		},
		removeGroup(groupId) {
			const groupKey = resolveIdKey(groupId);
			if (!groupKey) {
				return;
			}
			this.groups = this.groups.filter((group) => resolveIdKey(group && group.id) !== groupKey);
			this.friendPageByGroup.delete(groupKey);
			this.friendCacheByGroup.delete(groupKey);
			this.friendLoadTaskByGroup.delete(groupKey);
			this.page.total = Math.max(0, this.page.total - 1);
			this.rebuildMaps();
			getDB().deleteFriendGroup(groupId).catch((error) => {
				console.warn('删除好友分组本地快照失败', error);
			});
		},
		removeFriend(friendId) {
			const friendKey = resolveIdKey(friendId);
			if (!friendKey) {
				return;
			}
			const friendStore = useFriendStore();
			const friendEntity = friendStore.findFriend(friendId);
			const sourceGroupKey = resolveIdKey(friendEntity && friendEntity.groupId)
				|| this.friendToGroupMap.get(friendKey)
				|| '';
			const nextCacheMap = new Map(this.friendCacheByGroup);
			Array.from(nextCacheMap.keys()).forEach((groupKey) => {
				const cached = Array.isArray(nextCacheMap.get(groupKey)) ? nextCacheMap.get(groupKey) : [];
				nextCacheMap.set(groupKey, cached.filter((friend) => resolveIdKey(friend && friend.id) !== friendKey));
			});
			this.friendCacheByGroup = nextCacheMap;
			let dirtyGroupRecord = null;
			if (sourceGroupKey) {
				const sourceGroup = this.groupMap.get(sourceGroupKey);
				if (sourceGroup && Number.isFinite(Number(sourceGroup.memberCount))) {
					sourceGroup.memberCount = Math.max(0, Number(sourceGroup.memberCount) - 1);
					dirtyGroupRecord = sanitizeGroupForPersist(sourceGroup);
				}
			}
			if (friendEntity) {
				friendEntity.groupId = null;
			}
			this.rebuildMaps();
			if (dirtyGroupRecord) {
				getDB().saveFriendGroup(dirtyGroupRecord).catch((error) => {
					console.warn('更新好友分组成员数本地快照失败', error);
				});
			}
		},
		moveFriend(friendId, nextGroupId = null) {
			const friendKey = resolveIdKey(friendId);
			if (!friendKey) {
				return;
			}
			const friendStore = useFriendStore();
			const friendEntity = friendStore.findFriend(friendId);
			const sourceGroupKey = resolveIdKey(friendEntity && friendEntity.groupId)
				|| this.friendToGroupMap.get(friendKey)
				|| '';
			const nextCacheMap = new Map(this.friendCacheByGroup);
			Array.from(nextCacheMap.keys()).forEach((groupKey) => {
				const cached = Array.isArray(nextCacheMap.get(groupKey)) ? nextCacheMap.get(groupKey) : [];
				nextCacheMap.set(groupKey, cached.filter((friend) => resolveIdKey(friend && friend.id) !== friendKey));
			});
			const nextGroupKey = resolveIdKey(nextGroupId);
			const dirtyGroupRecords = [];
			if (sourceGroupKey && sourceGroupKey !== nextGroupKey) {
				const sourceGroup = this.groupMap.get(sourceGroupKey);
				if (sourceGroup && Number.isFinite(Number(sourceGroup.memberCount))) {
					sourceGroup.memberCount = Math.max(0, Number(sourceGroup.memberCount) - 1);
					const record = sanitizeGroupForPersist(sourceGroup);
					if (record) {
						dirtyGroupRecords.push(record);
					}
				}
			}
			if (nextGroupKey) {
				const targetGroup = this.groupMap.get(nextGroupKey);
				if (targetGroup && sourceGroupKey !== nextGroupKey && Number.isFinite(Number(targetGroup.memberCount))) {
					targetGroup.memberCount = Math.max(0, Number(targetGroup.memberCount) + 1);
					const record = sanitizeGroupForPersist(targetGroup);
					if (record) {
						dirtyGroupRecords.push(record);
					}
				}
				if (friendEntity) {
					const targetCache = Array.isArray(nextCacheMap.get(nextGroupKey)) ? nextCacheMap.get(nextGroupKey) : [];
					const exists = targetCache.some((item) => resolveIdKey(item && item.id) === friendKey);
					if (!exists) {
						nextCacheMap.set(nextGroupKey, targetCache.concat(friendEntity));
					}
				}
			}
			this.friendCacheByGroup = nextCacheMap;
			if (friendEntity) {
				friendEntity.groupId = nextGroupKey || null;
			}
			this.rebuildMaps();
			if (dirtyGroupRecords.length > 0) {
				getDB().saveFriendGroups(dirtyGroupRecords).catch((error) => {
					console.warn('更新好友分组成员数本地快照失败', error);
				});
			}
		},
		clear() {
			this.groups = [];
			this.groupMap = new Map();
			this.resetGroupListState();
			this.friendToGroupMap = new Map();
			this.friendPageByGroup = new Map();
			this.friendCacheByGroup = new Map();
			this.friendLoadTaskByGroup = new Map();
		},
		resolveGroupFriendPageState(groupId) {
			const groupKey = resolveIdKey(groupId);
			if (!groupKey) {
				return null;
			}
			const pageState = this.friendPageByGroup.get(groupKey);
			if (pageState) {
				return pageState;
			}
			return this.setGroupFriendPageState(groupKey, {});
		},
		setGroupFriendPageState(groupId, patch = {}) {
			const groupKey = resolveIdKey(groupId);
			if (!groupKey) {
				return null;
			}
			const current = this.friendPageByGroup.get(groupKey) || {
				loading: false,
				more: true,
				total: Math.max(0, Math.floor(Number((this.groupMap.get(groupKey) || {}).memberCount) || 0)),
				cursor: '',
				loaded: false,
				rev: 0,
				time: 0
			};
			const next = {
				...current,
				...(patch || {})
			};
			this.friendPageByGroup.set(groupKey, next);
			this.friendPageByGroup = new Map(this.friendPageByGroup);
			return next;
		},
		resolveGroupFriendCache(groupId) {
			const groupKey = resolveIdKey(groupId);
			if (!groupKey) {
				return [];
			}
			const cached = this.friendCacheByGroup.get(groupKey);
			return Array.isArray(cached) ? cached : [];
		},
		setGroupFriendCache(groupId, friends) {
			const groupKey = resolveIdKey(groupId);
			if (!groupKey) {
				return;
			}
			const seen = new Set();
			const normalized = [];
			(Array.isArray(friends) ? friends : []).forEach((friend) => {
				const next = normalizeFriendVO(friend);
				if (!next) {
					return;
				}
				const friendKey = resolveIdKey(next.id);
				if (!friendKey || seen.has(friendKey)) {
					return;
				}
				seen.add(friendKey);
				normalized.push(next);
			});
			this.friendCacheByGroup.set(groupKey, normalized);
			this.friendCacheByGroup = new Map(this.friendCacheByGroup);
		},
		appendGroupFriendCache(groupId, friends) {
			const merged = this.resolveGroupFriendCache(groupId).concat(Array.isArray(friends) ? friends : []);
			this.setGroupFriendCache(groupId, merged);
		},
		fetchGroupFriendsPage(groupId, { cursor = '', limit = 30, includeOnline = false } = {}) {
			const safeLimit = normalizePageLimit(limit, 30, FRIEND_GROUP_MEMBER_PAGE_LIMIT_MAX);
			const params = new URLSearchParams();
			params.set('limit', String(safeLimit));
			params.set('includeOnline', includeOnline ? 'true' : 'false');
			if (cursor) {
				params.set('cursor', String(cursor));
			}
			return http({
				url: `/friend/group/${groupId}/friends?${params.toString()}`,
				method: 'GET'
			}).then((payload) => normalizeFriendPagePayload(payload));
		},
		async loadGroupFriendsAll(groupId, options = {}) {
			const group = this.findGroup(groupId);
			if (!group || !group.id || Number(group.memberCount || 0) <= 0) {
				this.setGroupFriendCache(groupId, []);
				this.setGroupFriendPageState(groupId, {
					loaded: true,
					loading: false,
					more: false,
					total: 0,
					cursor: ''
				});
				return [];
			}
			const groupKey = resolveIdKey(group.id);
			const pageState = this.resolveGroupFriendPageState(group.id);
			if (!pageState) {
				return [];
			}
			const forceReload = options.forceReload === true;
			if (pageState.loading) {
				const runningTask = this.resolveGroupFriendTask(group.id);
				return runningTask || this.resolveGroupFriendCache(group.id);
			}
			if (!forceReload && pageState.loaded && !pageState.more) {
				return this.resolveGroupFriendCache(group.id);
			}
			const existingTask = this.resolveGroupFriendTask(group.id);
			if (existingTask) {
				return existingTask;
			}
			const safeLimit = Math.max(
				FRIEND_GROUP_MEMBER_BULK_PAGE_LIMIT,
				normalizePageLimit(
					options.limit,
					FRIEND_GROUP_MEMBER_BULK_PAGE_LIMIT,
					FRIEND_GROUP_MEMBER_PAGE_LIMIT_MAX
				)
			);
			const includeOnline = Boolean(options.includeOnline);
			const pageBudget = normalizePageBudget(
				options.maxPages,
				FRIEND_GROUP_MEMBER_FULL_LOAD_PAGE_BUDGET,
				FRIEND_GROUP_MEMBER_FULL_LOAD_PAGE_BUDGET_MAX
			);
			const loadTask = (async () => {
				const friendStore = useFriendStore();
				if (forceReload) {
					this.setGroupFriendCache(group.id, []);
					this.setGroupFriendPageState(group.id, {
						loaded: false,
						more: true,
						total: Math.max(0, Math.floor(Number(group.memberCount || 0))),
						cursor: ''
					});
				}
				this.setGroupFriendPageState(group.id, {
					loading: true
				});
				try {
					let runtimePageState = this.resolveGroupFriendPageState(group.id);
					let fetchedPages = 0;
					while (fetchedPages < pageBudget) {
						if (!runtimePageState) {
							break;
						}
						if (runtimePageState.loaded && !runtimePageState.more) {
							break;
						}
						const requestCursor = runtimePageState.loaded
							? String(runtimePageState.cursor || '')
							: '';
						const page = await this.fetchGroupFriendsPage(group.id, {
							cursor: requestCursor,
							limit: safeLimit,
							includeOnline
						});
						const items = (Array.isArray(page.items) ? page.items : [])
							.map((friend) => normalizeFriendVO(friend))
							.filter(Boolean);
						if (items.length > 0) {
							friendStore.append(items);
						}
						this.appendGroupFriendCache(group.id, items);
						const more = Boolean(page.more && page.cursor);
						runtimePageState = this.setGroupFriendPageState(group.id, {
							loaded: true,
							more,
							total: Number.isFinite(Number(page.total))
								? Math.max(0, Math.floor(Number(page.total)))
								: Math.max(0, Math.floor(Number(group.memberCount || 0))),
							cursor: more ? String(page.cursor) : ''
						});
						fetchedPages += 1;
						if (!more) {
							break;
						}
					}
					if (fetchedPages >= pageBudget) {
						this.setGroupFriendPageState(group.id, {
							loaded: true
						});
					}
					const runtimeTotal = Math.max(0, Math.floor(Number((this.resolveGroupFriendPageState(group.id) || {}).total || 0)));
					const loadedCount = this.resolveGroupFriendCache(group.id).length;
					if (runtimeTotal > 0 && loadedCount >= runtimeTotal) {
						this.setGroupFriendPageState(group.id, {
							more: false,
							cursor: '',
							loaded: true,
							total: Math.max(runtimeTotal, loadedCount)
						});
					}
					return this.resolveGroupFriendCache(group.id);
				} finally {
					this.setGroupFriendPageState(group.id, {
						loading: false
					});
					const runningTask = this.friendLoadTaskByGroup.get(groupKey);
					if (runningTask === loadTask) {
						this.setGroupFriendTask(group.id, null);
					}
				}
			})();
			this.setGroupFriendTask(group.id, loadTask);
			return loadTask;
		},
		async ensureGroupFriendsLoaded(groupId, options = {}) {
			return this.loadGroupFriendsAll(groupId, options);
		},
		async loadMoreGroupFriendsFromCursor(groupId, options = {}) {
			return this.loadGroupFriendsAll(groupId, options);
		},
		async ensureGroupFriendsFullyLoaded(groupId, options = {}) {
			return this.loadGroupFriendsAll(groupId, options);
		},
		resetGroupFriendCache(groupId) {
			const groupKey = resolveIdKey(groupId);
			if (!groupKey) {
				return;
			}
			this.setGroupFriendTask(groupId, null);
			this.setGroupFriendCache(groupId, []);
			this.setGroupFriendPageState(groupId, {
				loaded: false,
				loading: false,
				more: true,
				cursor: '',
				total: Math.max(0, Math.floor(Number((this.groupMap.get(groupKey) || {}).memberCount || 0)))
			});
		},
		fetchGroupListPage({ cursor = '', limit = FRIEND_GROUP_LIST_PAGE_LIMIT } = {}) {
			const safeLimit = Math.max(
				1,
				Math.min(
					FRIEND_GROUP_LIST_PAGE_LIMIT_MAX,
					normalizePageLimit(limit, FRIEND_GROUP_LIST_PAGE_LIMIT)
				)
			);
			const params = new URLSearchParams();
			params.set('limit', String(safeLimit));
			if (cursor) {
				params.set('cursor', String(cursor));
			}
			return http({
				url: `/friend/group/list?${params.toString()}`,
				method: 'GET'
			}).then((payload) => normalizeGroupPagePayload(payload));
		},
		loadGroups(options = {}) {
			if (this.tasks.load) {
				return this.tasks.load;
			}
			const forceReload = options.forceReload === true;
			if (!forceReload && this.groups.length > 0 && !this.page.error) {
				if (this.page.total <= 0) {
					this.page.total = this.groups.length;
				}
				if (this.page.total > 0 && this.groups.length >= this.page.total) {
					this.page.more = false;
					this.page.cursor = '';
				}
				return Promise.resolve(this.groups);
			}
			let resolveLocalReady = null;
			let rejectLocalReady = null;
			const localReadyPromise = new Promise((resolve, reject) => {
				resolveLocalReady = resolve;
				rejectLocalReady = reject;
			});
			this.tasks.localReady = localReadyPromise;
			const task = (async () => {
				const pageLimit = Math.max(
					1,
					Math.min(
						FRIEND_GROUP_LIST_PAGE_LIMIT_MAX,
						normalizePageLimit(options.limit, FRIEND_GROUP_LIST_FIRST_PAGE_LIMIT)
					)
				);
				const db = getDB();
				try {
					if (forceReload) {
						this.resetGroupListState({
							clearTasks: false
						});
					}
					if (this.groups.length === 0) {
						// 本地优先恢复：先用本地快照渲染，完整性由远端第一页确认。
						try {
							const localGroups = await db.findAllFriendGroups();
							if (Array.isArray(localGroups) && localGroups.length > 0) {
								this.setGroups(localGroups);
								this.page.total = localGroups.length;
								this.page.more = false;
								this.page.cursor = '';
							}
						} catch (error) {
							console.warn('恢复好友分组本地快照失败', error);
						}
					}
					resolveLocalReady && resolveLocalReady(this.groups);
				} catch (error) {
					rejectLocalReady && rejectLocalReady(error);
					throw error;
				}
				this.page.loading = true;
				this.page.error = false;
				try {
					const page = await this.fetchGroupListPage({
						cursor: '',
						limit: pageLimit
					});
					const pageTotal = Math.max(0, Math.floor(Number(page.total || 0)));
					const hasFullLocalSnapshot = this.groups.length > 0
						&& page.more
						&& pageTotal > 0
						&& this.groups.length === pageTotal;
					if (hasFullLocalSnapshot) {
						this.appendGroups(page.items);
					} else if (!page.more || forceReload || this.groups.length <= page.items.length || this.groups.length > pageTotal) {
						this.setGroups(page.items);
					} else {
						this.appendGroups(page.items);
					}
					this.setGroupListPageState(hasFullLocalSnapshot ? {
						...page,
						more: false,
						cursor: ''
					} : page);
					// 首页拉到全量时（没有 more），用 syncAll 整表覆盖；否则增量 bulkPut
					try {
						const records = (Array.isArray(page.items) ? page.items : [])
							.map(sanitizeGroupForPersist)
							.filter(Boolean);
						if (!page.more) {
							await db.syncAllFriendGroups(records);
						} else if (records.length > 0) {
							await db.saveFriendGroups(records);
						}
					} catch (error) {
						console.warn('落盘好友分组本地快照失败', error);
					}
					return this.groups;
				} catch (error) {
					this.page.error = true;
					throw error;
				} finally {
					this.page.loading = false;
				}
			})();
			const trackedTask = task.finally(() => {
				if (this.tasks.load === trackedTask) {
					this.tasks.load = null;
				}
				if (this.tasks.localReady === localReadyPromise) {
					this.tasks.localReady = null;
				}
			});
			this.tasks.load = trackedTask;
			return trackedTask;
		},
		loadGroupsLocalReady() {
			return this.tasks.localReady || Promise.resolve();
		},
		async loadMoreGroupsFromCursor(options = {}) {
			if (this.tasks.bg) {
				return this.tasks.bg;
			}
			if (this.tasks.load) {
				await this.tasks.load;
			}
			if (this.page.loading || !this.page.more || !this.page.cursor) {
				return {
					items: [],
					more: this.page.more
				};
			}
			const task = (async () => {
				const pageBudget = normalizePageBudget(options.maxPages, FRIEND_GROUP_LIST_BACKGROUND_PAGE_BUDGET, 100);
				const pageLimit = Math.max(
					1,
					Math.min(
						FRIEND_GROUP_LIST_PAGE_LIMIT_MAX,
						normalizePageLimit(options.limit, FRIEND_GROUP_LIST_PAGE_LIMIT)
					)
				);
				let cursor = this.page.cursor;
				let more = this.page.more;
				let fetchedPages = 0;
				let mergedItems = [];
				this.page.loading = true;
				this.page.error = false;
				try {
					while (more && cursor && fetchedPages < pageBudget) {
						const page = await this.fetchGroupListPage({
							cursor,
							limit: pageLimit
						});
						this.appendGroups(page.items);
						this.setGroupListPageState(page);
						try {
							const records = (Array.isArray(page.items) ? page.items : [])
								.map(sanitizeGroupForPersist)
								.filter(Boolean);
							if (records.length > 0) {
								await getDB().saveFriendGroups(records);
							}
						} catch (error) {
							console.warn('落盘好友分组分页快照失败', error);
						}
						cursor = this.page.cursor;
						more = this.page.more;
						mergedItems = mergedItems.concat(Array.isArray(page.items) ? page.items : []);
						fetchedPages += 1;
					}
					return {
						items: mergedItems,
						more: this.page.more
					};
				} catch (error) {
					this.page.error = true;
					throw error;
				} finally {
					this.page.loading = false;
				}
			})();
			const trackedTask = task.finally(() => {
				if (this.tasks.bg === trackedTask) {
					this.tasks.bg = null;
				}
			});
			this.tasks.bg = trackedTask;
			return trackedTask;
		},
		async ensureGroupListFullyLoaded(options = {}) {
			if (this.tasks.full) {
				return this.tasks.full;
			}
			const task = (async () => {
				const pageLimit = Math.max(
					1,
					Math.min(
						FRIEND_GROUP_LIST_PAGE_LIMIT_MAX,
						normalizePageLimit(options.limit, FRIEND_GROUP_LIST_PAGE_LIMIT)
					)
				);
				const maxPages = normalizePageBudget(options.maxPages, FRIEND_GROUP_LIST_FULL_LOAD_PAGE_BUDGET, 1000);
				if (this.tasks.load) {
					await this.tasks.load;
				}
				const knownTotalBeforeLoad = Math.max(0, Math.floor(Number(this.page.total || 0)));
				if (knownTotalBeforeLoad > 0 && !this.page.more && this.groups.length >= knownTotalBeforeLoad) {
					return this.groups;
				}
				await this.loadGroups({
					...options,
					limit: pageLimit
				});
				const knownTotal = Math.max(0, Math.floor(Number(this.page.total || 0)));
				if (knownTotal > 0 && !this.page.more && this.groups.length >= knownTotal) {
					this.page.more = false;
					this.page.cursor = '';
					return this.groups;
				}
				let loadedPages = 0;
				while (this.page.more && this.page.cursor && loadedPages < maxPages) {
					await this.loadMoreGroupsFromCursor({
						...options,
						limit: pageLimit,
						maxPages: 1
					});
					loadedPages += 1;
				}
				if (!this.page.more) {
					this.page.total = Math.max(this.page.total, this.groups.length);
					try {
						const records = this.groups.map(sanitizeGroupForPersist).filter(Boolean);
						await getDB().syncAllFriendGroups(records);
					} catch (error) {
						console.warn('落盘好友分组全量快照失败', error);
					}
				}
				return this.groups;
			})();
			const trackedTask = task.finally(() => {
				if (this.tasks.full === trackedTask) {
					this.tasks.full = null;
				}
			});
			this.tasks.full = trackedTask;
			return trackedTask;
		}
	},
	getters: {
		findGroup: (state) => (groupId) => {
			const groupKey = resolveIdKey(groupId);
			return groupKey ? state.groupMap.get(groupKey) : null;
		},
		findGroupByFriendId: (state) => (friendId) => {
			const friendKey = resolveIdKey(friendId);
			let groupKey = friendKey ? state.friendToGroupMap.get(friendKey) : '';
			if (!groupKey && friendKey) {
				const friendStore = useFriendStore();
				const friend = friendStore.findFriend(friendId);
				groupKey = resolveIdKey(friend && friend.groupId);
			}
			return groupKey ? state.groupMap.get(groupKey) : null;
		},
		findGroupFriends: (state) => (groupId) => {
			const groupKey = resolveIdKey(groupId);
			if (!groupKey) {
				return [];
			}
			const cached = state.friendCacheByGroup.get(groupKey);
			return Array.isArray(cached) ? cached : [];
		},
		isGroupFriendsLoading: (state) => (groupId) => {
			const groupKey = resolveIdKey(groupId);
			if (!groupKey) {
				return false;
			}
			const pageState = state.friendPageByGroup.get(groupKey);
			return Boolean(pageState && pageState.loading);
		},
		isGroupFriendsLoaded: (state) => (groupId) => {
			const groupKey = resolveIdKey(groupId);
			if (!groupKey) {
				return false;
			}
			const pageState = state.friendPageByGroup.get(groupKey);
			return Boolean(pageState && pageState.loaded);
		},
		getGroupFriendPageState: (state) => (groupId) => {
			const groupKey = resolveIdKey(groupId);
			if (!groupKey) {
				return null;
			}
			const pageState = state.friendPageByGroup.get(groupKey);
			if (!pageState) {
				return null;
			}
			return {
				loaded: Boolean(pageState.loaded),
				loading: Boolean(pageState.loading),
				more: Boolean(pageState.more && pageState.cursor),
				total: Number.isFinite(Number(pageState.total)) ? Math.max(0, Math.floor(Number(pageState.total))) : 0,
				cursor: pageState.cursor ? String(pageState.cursor) : ''
			};
		}
	}
});
